/**
 * Backend allergen/additive lists are free-form per restaurant: the same allergen arrives as
 * 'Milk Lactose', 'milkLactose', 'milk_lactose' or 'treeNut'. The catalogs
 * (resources/lang/<locale>/shared/Allergens.ts) are keyed in snake_case, so a lookup with the raw
 * value renders the raw value. This reduces any casing/spacing variant to the catalog's snake_case.
 */
export const toSnakeCaseKey = (value: string): string =>
  String(value ?? '')
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[^A-Za-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toLowerCase();

// Normalised (snake_case) variants seen in allergens_list that name a catalog key differently.
const ALLERGEN_ALIASES: Record<string, string> = {
  milk: 'dairy',
  milk_lactose: 'dairy',
  gluten_cereal: 'gluten',
  tree_nut: 'tree_nuts',
  almond: 'almonds',
  crustacean: 'crustaceans',
  soybean: 'soy',
  soya: 'soy',
};

/** Catalog key for an allergen value from the backend; unknown values come back normalised. */
export const normalizeAllergenKey = (value: string): string => {
  const key = toSnakeCaseKey(value);

  return ALLERGEN_ALIASES[key] ?? key;
};

/** Catalog key for an additive value from the backend (the catalog is already snake_case). */
export const normalizeAdditiveKey = (value: string): string => toSnakeCaseKey(value);
